import React from 'react';
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  VStack,
  Text,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { FaChartBar, FaDatabase, FaFileExport, FaUpload } from 'react-icons/fa';

const steps = [
  {
    icon: FaUpload,
    title: 'Upload your customer base',
    body:
      'Turn a CSV or XLSX customer file into a ranked behavioral dataset. The template-led upload uses required fields so teams can review bias scores per client without custom setup.',
    artifact: 'Artifact: bias scores per client, ranked.',
  },
  {
    icon: FaDatabase,
    title: 'Add your firm-ready data layer',
    body:
      'Surface product catalog, customer holdings, transactions, suitability evidence, and model portfolio targets against the same workspace. This gives every role firm-specific context instead of a generic demo dataset.',
    artifact: 'Artifact: tenant-specific product and client context.',
  },
  {
    icon: FaChartBar,
    title: 'Surface role-specific signals',
    body:
      'Queue what matters for each operating view: drift and risk mismatch for Portfolio Adviser, missing evidence for Compliance, and adoption or conversion signals for Head of Advisory. Provenance tags clarify computed, inferred, and unavailable values.',
    artifact: 'Artifact: per-role work queues.',
  },
  {
    icon: FaFileExport,
    title: 'Document, export, review',
    body:
      'Document decisions with PDF reports, CSV extracts, dashboard snapshots, and audit packs. Business workspaces can review evidence before internal reporting or stakeholder sharing.',
    artifact: 'Artifact: defendable internal review trail.',
  },
];

const HowItWorks = () => {
  return (
    <Box as="section" bg="brand.quantumBg" py={{ base: 16, md: 24 }} borderTopWidth="1px" borderColor="brand.quantumBorder">
      <Container maxW="container.xl">
        <Text
          display="inline-block"
          bg="rgba(0, 212, 170, 0.1)"
          color="brand.quantumAccent"
          fontSize="xs"
          fontWeight="800"
          letterSpacing="0.15em"
          textTransform="uppercase"
          px={3}
          py={1}
          borderRadius="full"
          mb={5}
        >
          Operating workflow
        </Text>
        <Heading
          as="h2"
          size="xl"
          mb={4}
          color="white"
          fontWeight="bold"
          textAlign={{ base: 'center', md: 'left' }}
        >
          How the platform creates value
        </Heading>
        <Text
          fontSize="lg"
          color="brand.quantumMuted"
          maxW="4xl"
          mb={{ base: 8, md: 12 }}
          textAlign={{ base: 'center', md: 'left' }}
        >
          FinanSee moves from raw client data to practical action by turning uploads into
          signals, surfacing role queues, and documenting the review trail.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={{ base: 6, md: 8 }}>
          {steps.map((step, index) => (
            <VStack
              key={step.title}
              bg="brand.quantumCard"
              p={6}
              borderRadius="2xl"
              boxShadow="0 18px 48px rgba(0, 0, 0, 0.28)"
              borderWidth="1px"
              borderColor="brand.quantumBorder"
              align="start"
              spacing={4}
            >
              <HStack spacing={3} align="center">
                <Box
                  w="38px"
                  h="38px"
                  borderRadius="12px"
                  bg="rgba(0, 212, 170, 0.1)"
                  color="brand.quantumAccent"
                  display="grid"
                  placeItems="center"
                >
                  <Icon as={step.icon} boxSize={4} />
                </Box>
                <Text color="brand.quantumAccent" fontSize="xs" fontWeight="800" letterSpacing="0.12em">
                  STEP {index + 1}
                </Text>
              </HStack>
              <Heading size="md" color="white">
                {step.title}
              </Heading>
              <Text color="brand.quantumMuted" fontSize="md">
                {step.body}
              </Text>
              <Text color="brand.quantumSubtle" fontSize="sm" mt="auto">
                {step.artifact}
              </Text>
            </VStack>
          ))}
        </SimpleGrid>

        <Box
          mt={8}
          bg="rgba(255, 255, 255, 0.04)"
          borderWidth="1px"
          borderColor="brand.quantumBorder"
          borderRadius="2xl"
          p={5}
        >
          <Text color="brand.quantumMuted" fontSize="sm">
            Steps 1 and 3 are live in the Playground today. Step 2 is the v2 firm-ready
            data layer. Step 4 is the audit and export surface available on Business.
          </Text>
        </Box>
      </Container>
    </Box>
  );
};

export default HowItWorks;
